import { pluralize } from "@inrev/common";
import { Option } from "@inrev/core";
import {
	categoryLabel,
	derogatoryCount,
	type ExperianCreditDetail,
	formatCreditAge,
	formatMonthYear,
	formatReportDollars,
} from "@portal/api/account/admin/individualCreditReport";
import { MetricCard } from "@portal/components/patterns";
import { Card, Text } from "@portal/components/primitives";
import type { FC } from "react";
import { formatEmpty } from "../../../../../../../utils";
import { ReportSection, SectionSummary } from "./ReportSection";

type Detail = ExperianCreditDetail;

type StatCardProps = {
	label: string;
	value: Option<string>;
	caption: Option<string>;
};

const StatCard: FC<StatCardProps> = ({ label, value, caption }) => (
	<MetricCard
		data-testid={`individual_credit_summary_${label.toLowerCase().replace(/[^a-z]+/g, "_")}`}
	>
		<MetricCard.Label size="md">{label}</MetricCard.Label>
		<MetricCard.Value size="md">{formatEmpty(value)}</MetricCard.Value>
		{Option.isSome(caption) && <MetricCard.Caption>{caption}</MetricCard.Caption>}
	</MetricCard>
);

export const CreditSummarySection: FC<{ detail: Detail }> = ({ detail }) => {
	const { summary, publicRecords } = detail;
	const { bankruptcies, liens, judgments, collections } = publicRecords;
	const derogatoryBreakdown = [
		pluralize(bankruptcies.length, "bankruptcy", "bankruptcies"),
		pluralize(collections.length, "collection", "collections"),
		...(liens.length > 0 ? [pluralize(liens.length, "lien", "liens")] : []),
		...(judgments.length > 0 ? [pluralize(judgments.length, "judgment", "judgments")] : []),
	].join(" · ");
	const accountsWithBalance = detail.tradelines.filter(
		(tradeline) => (tradeline.balance ?? 0) > 0,
	).length;

	return (
		<ReportSection
			title="Credit Summary"
			testId="individual_credit_summary"
			summary={
				<SectionSummary>
					{pluralize(summary.tradelines.total, "account", "accounts")} ·{" "}
					{formatReportDollars(summary.totalDebt)} total debt
				</SectionSummary>
			}
		>
			<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
				<StatCard
					label="Total Debt"
					value={formatReportDollars(summary.totalDebt)}
					caption={`Across ${pluralize(accountsWithBalance, "account", "accounts")} with a balance`}
				/>
				<StatCard
					label="Credit Utilization"
					value={Option.map(summary.utilizationPct, (pct) => `${pct}%`)}
					caption={
						summary.revolvingLimit > 0
							? `${formatReportDollars(summary.revolvingBalance)} of ${formatReportDollars(summary.revolvingLimit)} revolving`
							: "No revolving credit"
					}
				/>
				<StatCard
					label="Monthly Payments"
					value={formatReportDollars(summary.monthlyPayments)}
					caption="Scheduled, all accounts"
				/>
				<StatCard
					label="On-Time Payments"
					value={Option.map(summary.onTimePct, (pct) => `${pct}%`)}
					caption="Of reported payments"
				/>
				<StatCard
					label="Delinquencies"
					value={`${summary.delinquencies.late30} · ${summary.delinquencies.late60} · ${summary.delinquencies.late90Plus}`}
					caption="30 / 60 / 90–180 day lates"
				/>
				<StatCard
					label="Derogatory Items"
					value={String(derogatoryCount(detail))}
					caption={derogatoryBreakdown}
				/>
				<StatCard
					label="Credit Age"
					value={formatCreditAge(summary.oldestOpenedOn)}
					caption={Option.map(
						summary.oldestOpenedOn,
						(opened) => `Oldest opened ${formatMonthYear(opened)}`,
					)}
				/>
				<StatCard
					label="Tradelines"
					value={String(summary.tradelines.total)}
					caption={[
						`${summary.tradelines.open} open`,
						`${summary.tradelines.closed} closed`,
						...(collections.length > 0
							? [pluralize(collections.length, "collection", "collections")]
							: []),
					].join(" · ")}
				/>
			</div>
			{summary.mix.length > 0 && (
				<Card
					className="grid grid-cols-2 rounded-xl bg-surface-light lg:grid-cols-4"
					data-testid="individual_credit_account_mix"
				>
					{summary.mix.map((entry) => (
						<div
							key={entry.category}
							className="flex flex-col gap-2.5 border-bc-default px-5 py-3.5 lg:border-r lg:last:border-r-0"
						>
							<Text type="eyebrow" color="tertiary">
								{categoryLabel(entry.category)}
							</Text>
							<Text type="title" color="primary">
								{formatReportDollars(entry.balance)}
							</Text>
							<Text type="caption" color="secondary">
								{pluralize(entry.count, "account", "accounts")}
							</Text>
						</div>
					))}
				</Card>
			)}
		</ReportSection>
	);
};
